/**
 * Small, deterministic live-analysis baseline.  It deliberately works on
 * plain data so callers can pass a snapshot from the replay engine without
 * giving the search permission to mutate playback state.
 */
import { advancePhysics } from './haxballPhysics';

export const LIVE_ANALYSIS_VERSION = 'rules-physics-v2';
export const POSSESSION_PLANNER_VERSION = 'movement-release-planner-v2';

const BALL_RADIUS = 10;
const PLAYER_RADIUS = 15;
// HaxBall's kick test is `distance - playerRadius - ballRadius < 4`.
// A larger planning radius makes the UI suggest impossible kicks.
const KICK_CONTACT_GAP = 4;
const MAX_CANDIDATES = 16;
const HORIZON = 45;
const PLAN_HORIZON = 18;
const SETUP_SPEED = 2.5;
const DEFENDER_REACTION_TICKS = 6;

const number = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const point = (value) => {
  if (!value) return { x: 0, y: 0 };
  if (Array.isArray(value)) return { x: number(value[0]), y: number(value[1]) };
  return { x: number(value.x ?? value.position?.x ?? value.a?.x), y: number(value.y ?? value.position?.y ?? value.a?.y) };
};

const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
const normalise = (v) => {
  const length = Math.hypot(v.x, v.y) || 1;
  return { x: v.x / length, y: v.y / length };
};
const clone = (value) => {
  if (value === undefined) return undefined;
  if (typeof structuredClone === 'function') {
    try { return structuredClone(value); } catch (_) {}
  }
  try { return JSON.parse(JSON.stringify(value)); } catch (_) { return value; }
};

const pointDistanceToSegment = (pointValue, start, end) => {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const lengthSquared = dx * dx + dy * dy;
  if (!lengthSquared) return distance(pointValue, start);
  const t = Math.max(0, Math.min(1, ((pointValue.x - start.x) * dx + (pointValue.y - start.y) * dy) / lengthSquared));
  return distance(pointValue, { x: start.x + t * dx, y: start.y + t * dy });
};

const segmentBlocked = (start, end, opponents, clearance = 28) => opponents.some((opponent) => {
  const position = point(opponent.position || opponent.pos);
  return pointDistanceToSegment(position, start, end) < clearance;
});

function stadiumGoals(stadium = {}, state = {}) {
  const width = number(stadium.bg?.width ?? stadium.width, 840);
  const explicit = state.goals || stadium.goals || {};
  const list = Array.isArray(explicit) ? explicit : Object.entries(explicit).map(([team, value]) => ({ team, value }));
  const result = {};
  const goalPoint = (goal) => {
    if (!goal || typeof goal !== 'object') return point(goal);
    // Haxball serialises goals as line segments (p0/p1).  Aim at the
    // centre of the mouth instead of falling back to (0, 0).
    if (goal.p0 !== undefined || goal.p1 !== undefined) {
      const p0 = point(goal.p0); const p1 = point(goal.p1 ?? goal.p0);
      return { x: (p0.x + p1.x) / 2, y: (p0.y + p1.y) / 2 };
    }
    return point(goal.position || goal.pos || goal.value || goal);
  };
  list.forEach((goal) => {
    const team = String(goal?.team ?? goal?.color ?? goal?.side ?? '').toLowerCase();
    if (!['red', 'blue'].includes(team)) return;
    result[team] = goalPoint(goal.value || goal);
  });
  if (!result.red) result.red = { x: width, y: 0 };
  if (!result.blue) result.blue = { x: -width, y: 0 };
  return result;
}

function deepFreeze(value, seen = new Set()) {
  if (!value || typeof value !== 'object' || seen.has(value)) return value;
  seen.add(value);
  Object.freeze(value);
  Object.keys(value).forEach((key) => deepFreeze(value[key], seen));
  return value;
}

/** Return a frozen, normalised copy suitable for branch simulation. */
export function adaptAuthoritativeState(raw = {}) {
  const state = clone(raw) || {};
  const stadium = state.stadium || state.map || {};
  const ballSource = state.ball || state.ballState || state.disc || {};
  const playersSource = Array.isArray(state.players) ? state.players
    : (Array.isArray(state.playerStates) ? state.playerStates : []);
  const defaultKickStrength = number(stadium.playerPhysics?.kickStrength ?? stadium.ge?.Re, 5);
  const players = playersSource.map((entry, index) => {
    const p = entry || {};
    const position = point(p.position || p.pos || p.H?.a);
    const velocity = point(p.velocity || p.vel || p.H?.D);
    return {
      id: p.id ?? p.playerId ?? p.V ?? index,
      name: p.name ?? p.nick ?? p.w ?? '',
      team: p.team ?? p.teamKey ?? p.ea?.w ?? null,
      position,
      velocity,
      radius: number(p.radius ?? p.H?.Z, PLAYER_RADIUS),
      cMask: clone(p.cMask ?? p.h ?? p.H?.h),
      cGroup: clone(p.cGroup ?? p.v ?? p.H?.v),
      bCoef: number(p.bCoef ?? p.H?.m ?? stadium.playerPhysics?.bCoef, .5),
      invMass: number(p.invMass ?? p.H?.aa ?? stadium.playerPhysics?.invMass, .5),
      damping: number(p.damping ?? p.H?.Ca ?? stadium.playerPhysics?.damping, .96),
      gravity: point(p.gravity || p.H?.oa || stadium.playerPhysics?.gravity),
      input: { x: number(p.input?.x ?? p.inputX), y: number(p.input?.y ?? p.inputY) },
      kickCooldown: number(p.kickCooldown ?? p.cooldown ?? p.Sc),
      kickHeld: Boolean(p.kickHeld),
      kickBudget: p.kickBudget ?? p.yc,
      // Older snapshots expose kickStrength on the player; replay snapshots
      // expose the authoritative value on stadium.playerPhysics.
      kickStrength: number(p.kickStrength ?? p.playerPhysics?.kickStrength, defaultKickStrength),
      kickRate: clone(p.kickRate || state.kickRate || {}),
      kickback: number(p.kickback ?? stadium.playerPhysics?.kickback),
      facing: point(p.facing || p.direction || p.input || p.H?.D),
      active: p.active !== false && p.spectator !== true && p.H !== null
    };
  });
  const goals = stadiumGoals(stadium, state);
  const result = {
    version: LIVE_ANALYSIS_VERSION,
    tick: number(state.tick ?? state.replayTick ?? state.frame),
    stadium: clone(stadium) || {},
    ball: {
      position: point(ballSource.position || ballSource.pos || ballSource.a),
      velocity: point(ballSource.velocity || ballSource.vel || ballSource.D),
      radius: number(ballSource.radius, BALL_RADIUS),
      cMask: clone(ballSource.cMask ?? ballSource.h),
      cGroup: clone(ballSource.cGroup ?? ballSource.v),
      invMass: number(ballSource.invMass ?? ballSource.aa, 1),
      bCoef: number(ballSource.bCoef ?? ballSource.m, 0.5),
      damping: number(ballSource.damping ?? ballSource.damp ?? ballSource.Ca, 0.99),
      gravity: point(ballSource.gravity || ballSource.oa)
    },
    players,
    goals,
    attackDirection: clone(state.attackDirection || {}),
    score: clone(state.score || { red: number(state.scoreRed), blue: number(state.scoreBlue) }),
    timeRemaining: number(state.timeRemaining ?? state.time ?? state.clock, 0),
    paused: Boolean(state.paused),
    matchId: state.matchId ?? state.replayId ?? null
  };
  return deepFreeze(result);
}

const mapName = (stadium = {}) => String(stadium.name || stadium.mapName || '').trim();

/** Require both a known Futsal family and usable bouncing sideline geometry. */
export function isEligibleFutsalMap(input = {}) {
  const stadium = input.stadium || input.map || input;
  const name = mapName(stadium);
  if (!/(futsal|ffl|bff\s*big|bff)/i.test(name) || (/(classic|hockey|real\s*soccer)/i.test(name) && input.allowClassic !== true)) return false;
  const bg = stadium.bg || {};
  const width = number(bg.width ?? stadium.width);
  const height = number(bg.height ?? stadium.height);
  if (!(width > 0 && height > 0) || !Array.isArray(stadium.segments)) return false;
  const vertexes = Array.isArray(stadium.vertexes) ? stadium.vertexes : [];
  let sidelineCount = 0;
  stadium.segments.forEach((segment) => {
    if (segment?.vis === false) return;
    const v0 = vertexes[segment.v0] || segment.a || segment.p0;
    const v1 = vertexes[segment.v1] || segment.b || segment.p1;
    if (!v0 || !v1) return;
    const a = point(v0); const b = point(v1);
    const nearHorizontal = Math.abs(a.y - b.y) < height * 0.08 && Math.abs(Math.abs(a.y) - height) < height * 0.2;
    const nearVertical = Math.abs(a.x - b.x) < width * 0.08 && Math.abs(Math.abs(a.x) - width) < width * 0.2;
    const collidesBall = Array.isArray(segment.cMask)
      ? (segment.cMask.includes('ball') || segment.cMask.includes('all'))
      : (segment.cMask === undefined || number(segment.cMask) !== 0);
    const bCoef = number(segment.bCoef ?? segment.bounce, 1);
    if ((nearHorizontal || nearVertical) && collidesBall && bCoef > 0) sidelineCount += 1;
  });
  return sidelineCount >= 2;
}

function teamOf(player) { return String(player?.team || '').toLowerCase(); }

const clampNumber = (value, min, max) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return numeric === Infinity ? max : min;
  return Math.max(min, Math.min(max, numeric));
};
const add = (a, b) => ({ x: a.x + b.x, y: a.y + b.y });
const scale = (a, amount) => ({ x: a.x * amount, y: a.y * amount });
const subtract = (a, b) => ({ x: a.x - b.x, y: a.y - b.y });

function attackGoalFor(state, player) {
  const team = teamOf(player);
  const opponent = team === 'red' ? 'blue' : 'red';
  const width = number(state.stadium?.bg?.width ?? state.stadium?.width, 840);
  return state.goals?.[opponent] || { x: opponent === 'blue' ? -width : width, y: 0 };
}

function projectedPath(start, target, steps) {
  const count = Math.max(2, Math.round(steps));
  return Array.from({ length: count + 1 }, (_, index) => {
    const t = index / count;
    return { x: start.x + (target.x - start.x) * t, y: start.y + (target.y - start.y) * t };
  });
}

// A kick follows the carrier-to-ball vector. Walking straight to the other
// side of the ball would collide with it, so a setup must go around the disc.
// This is a bounded geometric estimate; the replay engine remains authoritative.
function kickSetup(player, ball, direction) {
  const offset = subtract(player.position, ball.position);
  const radius = Math.hypot(offset.x, offset.y);
  const contactRadius = player.radius + ball.radius;
  const currentDirection = normalise(scale(offset, -1));
  const aligned = currentDirection.x * direction.x + currentDirection.y * direction.y >= .9995;
  if (radius > 0 && radius <= contactRadius + 4 && aligned) {
    return { kickPosition: { ...player.position }, setupPath: [{ ...player.position }], setupTicks: 0 };
  }
  const clearance = contactRadius + .75;
  const destination = add(ball.position, scale(direction, -clearance));
  const line = (start, end) => {
    const count = Math.max(1, Math.ceil(distance(start, end) / SETUP_SPEED));
    return Array.from({ length: count + 1 }, (_, index) => add(start, scale(subtract(end, start), index / count)));
  };
  let setupPath;
  if (pointDistanceToSegment(ball.position, player.position, destination) >= contactRadius) {
    setupPath = line(player.position, destination);
  } else {
    const startAngle = radius > 0 ? Math.atan2(offset.y, offset.x) : Math.atan2(-direction.y, -direction.x);
    const endAngle = Math.atan2(-direction.y, -direction.x);
    const tangentOffset = Math.acos(clearance / Math.max(clearance, radius));
    const routes = [-1, 1].map(sign => {
      const tangentAngle = startAngle + sign * tangentOffset;
      const tangent = add(ball.position, { x: Math.cos(tangentAngle) * clearance, y: Math.sin(tangentAngle) * clearance });
      const approach = line(player.position, tangent);
      const turn = ((sign * (endAngle - tangentAngle)) % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI);
      const count = Math.max(1, Math.ceil(turn * clearance / SETUP_SPEED));
      const arc = Array.from({ length: count }, (_, index) => {
        const angle = tangentAngle + sign * turn * (index + 1) / count;
        return add(ball.position, { x: Math.cos(angle) * clearance, y: Math.sin(angle) * clearance });
      });
      return approach.concat(arc);
    });
    setupPath = routes.sort((a, b) => a.length - b.length)[0];
    setupPath[setupPath.length - 1] = destination;
  }
  return { kickPosition: destination, setupPath, setupTicks: setupPath.length - 1 };
}

function setupResponseRisk(state, player, candidate) {
  if (!player || candidate.type === 'retain') return { setupExposure: 0, laneClosureRisk: 0, reactionTicks: DEFENDER_REACTION_TICKS };
  const opponents = state.players.filter(other => other.active !== false && teamOf(other) && teamOf(other) !== teamOf(player));
  const ticks = Math.max(0, number(candidate.setupTicks));
  let setupExposure = 0;
  let laneClosureRisk = 0;
  // Project observed velocity, then allow a bounded change of direction after
  // a reaction delay. This measures an opportunity to close space, not an
  // assertion that the defender will choose that movement.
  opponents.forEach(opponent => {
    for (let tick = 1; tick <= ticks; tick += 1) {
      const coastTicks = Math.min(tick, DEFENDER_REACTION_TICKS);
      const projected = add(opponent.position, scale(opponent.velocity, coastTicks));
      const reach = opponent.radius + state.ball.radius + SETUP_SPEED * Math.max(0, tick - DEFENDER_REACTION_TICKS);
      setupExposure = Math.max(setupExposure, clampNumber((reach + 15 - distance(projected, state.ball.position)) / 30, 0, 1));
    }
    if (candidate.target) {
      const projected = add(opponent.position, scale(opponent.velocity, Math.min(ticks, DEFENDER_REACTION_TICKS)));
      const reach = opponent.radius + state.ball.radius + SETUP_SPEED * Math.max(0, ticks - DEFENDER_REACTION_TICKS);
      const laneDistance = pointDistanceToSegment(projected, state.ball.position, candidate.target);
      laneClosureRisk = Math.max(laneClosureRisk, clampNumber((reach + 10 - laneDistance) / 25, 0, 1));
    }
  });
  return { setupExposure, laneClosureRisk, reactionTicks: DEFENDER_REACTION_TICKS };
}

function nearestOpponent(state, player) {
  return state.players
    .filter(other => other.active !== false && teamOf(other) && teamOf(other) !== teamOf(player))
    .sort((a, b) => distance(a.position, player.position) - distance(b.position, player.position))[0] || null;
}

/**
 * Build short, executable carry/shield/reposition plans before selecting a kick.
 * The planner only uses the current authoritative snapshot. It never reads
 * future replay frames, so recommendations cannot leak the recorded outcome.
 */
export function planPossession(snapshot, playerId, options = {}) {
  const state = adaptAuthoritativeState(snapshot);
  const player = state.players.find(p => p.active !== false && (String(p.id) === String(playerId) || String(p.name).trim().toLowerCase() === String(playerId ?? '').trim().toLowerCase()));
  if (!player || !state.ball) return { version: POSSESSION_PLANNER_VERSION, plans: [], best: null };
  const ball = state.ball.position;
  const goal = attackGoalFor(state, player);
  const attack = normalise(subtract(goal, ball));
  const side = { x: -attack.y, y: attack.x };
  const opponent = nearestOpponent(state, player);
  const away = opponent ? normalise(subtract(player.position, opponent.position)) : side;
  const width = number(state.stadium?.bg?.width ?? state.stadium?.width, 840);
  const height = number(state.stadium?.bg?.height ?? state.stadium?.height, 420);
  const pressureDistance = opponent ? distance(opponent.position, ball) : Infinity;
  const pressure = clampNumber((100 - pressureDistance) / 100, 0, 1);
  const teammates = state.players.filter(p => p.active !== false && p.id !== player.id && teamOf(p) === teamOf(player));
  const openTeammate = teammates
    .filter(mate => !segmentBlocked(ball, mate.position, state.players.filter(p => teamOf(p) && teamOf(p) !== teamOf(player))))
    .sort((a, b) => distance(a.position, ball) - distance(b.position, ball))[0];
  const freeDistance = Number.isFinite(pressureDistance) ? clampNumber(180 - pressureDistance, 45, 180) : 180;
  const targets = [
    { type: 'carry-forward', label: 'Carry forward', target: add(ball, scale(attack, freeDistance)), trigger: 'Release when the defender commits or a forward lane opens.' },
    { type: 'carry-sideways', label: 'Carry sideways', target: add(ball, scale(side, 95)), trigger: 'Release when the lateral lane clears or the defender turns.' },
    { type: 'shield', label: 'Shield the ball', target: add(ball, scale(away, 42)), trigger: 'Release when a teammate becomes available; abandon if pressure reaches kick range.' },
    { type: 'retreat', label: 'Retreat and reset', target: add(ball, scale(attack, -100)), trigger: 'Release after the team shape resets or the pressure line breaks.' },
    { type: 'wall-approach', label: 'Approach the wall', target: { x: ball.x + attack.x * 70, y: (ball.y >= 0 ? 1 : -1) * height * .78 }, trigger: 'Release for a wall action when the direct lane is blocked.' }
  ];
  if (openTeammate) targets.push({ type: 'draw-and-pass', label: 'Draw pressure then pass', target: add(ball, scale(attack, 65)), trigger: 'Pass as soon as the defender steps toward the carrier and the receiver lane opens.', receiverId: openTeammate.id });
  const plans = targets.map((plan, index) => {
    const target = { x: clampNumber(plan.target.x, -width * .96, width * .96), y: clampNumber(plan.target.y, -height * .96, height * .96) };
    const distanceToTarget = distance(player.position, target);
    const ticks = clampNumber(Math.ceil(distanceToTarget / 5), 6, PLAN_HORIZON);
    const path = projectedPath(player.position, target, ticks);
    // Preserve the observed player-to-ball offset. Shrinking that offset to
    // zero placed both discs at the same centre and destroyed kick direction.
    const controlOffset = subtract(ball, player.position);
    const ballPath = path.map(position => add(position, controlOffset));
    const projected = clone(state);
    const projectedPlayer = projected.players.find(p => p.id === player.id);
    if (projectedPlayer) projectedPlayer.position = { ...target };
    projected.ball.position = { ...ballPath[ballPath.length - 1] };
    const releaseCandidates = generateCandidates(projected, player.id, { maxCandidates: 8 })
      .map(candidate => ({ ...candidate, playerId: player.id }));
    const releaseSimulations = releaseCandidates.map(candidate => simulateCandidate(projected, candidate, { playerId: player.id, horizon: Math.min(HORIZON, 30) }));
    const releases = rankCandidates(projected, releaseSimulations);
    // A release is executable when the player can reach the correct kick
    // position within the bounded plan. The player does not need to already
    // face the final trajectory at the pause tick; that is exactly what the
    // carry/setup path is meant to accomplish.
    const release = releases.find(option => option.type !== 'retain' && Number(option.setupTicks) <= PLAN_HORIZON) || null;
    const releaseAlignment = release ? 1 : 0;
    const executableRelease = release;
    const progress = (target.x - player.position.x) * (attack.x >= 0 ? 1 : -1);
    const laneBonus = openTeammate ? .35 : 0;
    const pressureCost = plan.type === 'shield' ? pressure * .2 : pressure * .55;
    const setupPath = executableRelease?.kickPosition && projectedPlayer
      ? projectedPath(target, executableRelease.kickPosition, Math.max(2, executableRelease.setupTicks))
      : [];
    const fullMovementPath = setupPath.length > 1 ? path.concat(setupPath.slice(1)) : path;
    const score = number(release?.score, 0) + progress / width + laneBonus - pressureCost - fullMovementPath.length / 100;
    return {
      id: `plan-${plan.type}-${index}`, type: plan.type, label: plan.label,
      estimated: true, physicsValidated: false,
      movementPath: fullMovementPath, path, ballPath, target, durationTicks: fullMovementPath.length - 1,
      releaseAtTick: number(state.tick, 0) + fullMovementPath.length - 1, release: executableRelease, releaseOptions: releases.slice(0, 3),
      receiverId: plan.receiverId || release?.receiverId || null, trigger: plan.trigger,
      score: score - (executableRelease ? 0 : .65), confidence: clampNumber(.38 + (1 - pressure) * .28 + (executableRelease ? .2 : 0) + (openTeammate ? .1 : 0), 0, .95),
      risk: pressure > .6 ? 'High turnover risk if the carrier delays beyond the release trigger.' : 'Holding too long can close the next lane.',
      metrics: { pressure, pressureDistance, progress, durationTicks: fullMovementPath.length - 1, openTeammate: Boolean(openTeammate), releaseScore: executableRelease?.score ?? null, releaseAlignment, setupTicks: executableRelease?.setupTicks ?? null }
    };
  }).sort((a, b) => b.score - a.score);
  return { version: POSSESSION_PLANNER_VERSION, plans, best: plans[0] || null, horizon: PLAN_HORIZON, usesFutureFrames: false };
}
function controlsFor(direction) {
  const d = normalise(direction);
  const candidates = [
    { x: 1, y: 0 }, { x: -1, y: 0 }, { x: 0, y: 1 }, { x: 0, y: -1 },
    { x: 1, y: 1 }, { x: 1, y: -1 }, { x: -1, y: 1 }, { x: -1, y: -1 }
  ];
  return candidates.reduce((best, input) => {
    const score = input.x * d.x + input.y * d.y;
    return score > best.score ? { input, score } : best;
  }, { input: { x: 0, y: 0 }, score: -Infinity }).input;
}

/** Generate only actions executable from the supplied authoritative state. */
export function generateCandidates(snapshot, playerId, options = {}) {
  const state = adaptAuthoritativeState(snapshot);
  const requested = String(playerId ?? '').trim().toLowerCase();
  const player = state.players.find((p) => p.active !== false && (String(p.id) === String(playerId) || String(p.name).trim().toLowerCase() === requested));
  if (!player) return [];
  const ball = state.ball.position;
  // Keep the strict inequality used by the engine. At exactly four units of
  // gap the kick is rejected, which matters for the close pressure cases.
  const kickable = distance(player.position, ball) - player.radius - state.ball.radius < KICK_CONTACT_GAP;
  if (!kickable) return [];
  const teammates = state.players.filter((p) => p.id !== player.id && teamOf(p) === teamOf(player));
  const opponents = state.players.filter((p) => p.active !== false && teamOf(p) && teamOf(p) !== teamOf(player));
  const width = number(state.stadium?.bg?.width ?? state.stadium?.width, 840);
  const height = number(state.stadium?.bg?.height ?? state.stadium?.height, 420);
  const target = (type, targetPoint, extra = {}) => {
    const direction = normalise({ x: targetPoint.x - ball.x, y: targetPoint.y - ball.y });
    // Haxball kick direction is the player-to-ball vector.  A candidate may
    // therefore need a short, explicit movement setup before the kick.
    const setup = kickSetup(player, state.ball, direction);
    const kickPosition = setup.kickPosition;
    const setupTicks = Math.max(extra.setupTicks || 0, setup.setupTicks);
    return {
      ...extra,
      id: `${type}-${Math.round(targetPoint.x)}-${Math.round(targetPoint.y)}`,
      type,
      target: { ...targetPoint },
      direction,
      controls: controlsFor({ x: kickPosition.x - player.position.x, y: kickPosition.y - player.position.y }),
      setupTicks,
      kickPosition,
      setupPath: setup.setupPath,
      executable: true
    };
  };
  const retainDirection = normalise({ x: ball.x - player.position.x, y: ball.y - player.position.y });
  // Retain is a control action, not a kick toward an artificial waypoint. The
  // carrier keeps its current ball-facing direction and the shared physics
  // stepper lets player-ball contact move the ball naturally.
  const candidates = [{
    id: 'retain', type: 'retain', target: { ...ball }, direction: retainDirection,
    controls: controlsFor(retainDirection), setupTicks: 0, kickPosition: null,
    setupPath: [{ ...player.position }], executable: true
  }];
  // A player whose kick cooldown is active can still retain or reposition,
  // but suggesting a kick/pass/shot would be impossible at this tick.
  const canKick = number(player.kickCooldown, 0) <= 0;
  if (canKick) teammates.slice(0, 5).forEach((mate) => {
    const blocked = segmentBlocked(ball, mate.position, opponents);
    candidates.push(target('pass', mate.position, { receiverId: mate.id, setupTicks: 2, laneBlocked: blocked, interceptionRisk: blocked ? 1 : 0 }));
  });
  const team = teamOf(player);
  // A team's attacking target is the opponent's goal. Goal metadata is keyed
  // by the defending team in replay stadium geometry.
  const opponentTeam = team === 'red' ? 'blue' : 'red';
  const goal = state.goals?.[opponentTeam] || { x: team === 'blue' ? width : -width, y: 0 };
  if (canKick) {
    // A correctly aligned carrier can shoot on this tick. kickSetup adds a
    // setup only when the carrier is on the wrong side of the ball.
    candidates.push(target('shot', goal, { setupTicks: 0, goal: true }));
    candidates.push(target('pass-space', { x: ball.x + retainDirection.x * width * 0.35, y: ball.y + retainDirection.y * height * 0.2 }, { setupTicks: 2 }));
    const wallY = ball.y >= 0 ? height : -height;
    candidates.push(target('wall-pass', { x: ball.x + retainDirection.x * 100, y: wallY }, { setupTicks: 6, wall: true }));
    candidates.push(target('wall-self-pass', { x: ball.x + retainDirection.x * 75, y: wallY * 0.9 }, { setupTicks: 7, wall: true, self: true }));
  }
  const pressure = opponents.reduce((n, p) => Math.min(n, distance(p.position, ball)), Infinity);
  return candidates
    .filter((candidate) => !candidate.laneBlocked || candidate.type !== 'pass')
    .slice(0, Math.min(MAX_CANDIDATES, number(options.maxCandidates, MAX_CANDIDATES))).map((candidate) => ({
    ...candidate,
    pressure: Number.isFinite(pressure) ? pressure : null
  }));
}

const integratePlayer = (player, target, physics = {}) => {
  if (!player) return;
  const delta = subtract(target, player.position);
  const length = Math.hypot(delta.x, delta.y);
  const input = length > 1.5 ? { x: delta.x / length, y: delta.y / length } : { x: 0, y: 0 };
  // O.C applies acceleration before Fa.C advances the disc and damps it.
  const acceleration = number(physics.acceleration ?? physics.Ce, 0.1);
  const damping = number(physics.damping ?? physics.Ca, 0.96);
  player.velocity.x += input.x * acceleration;
  player.velocity.y += input.y * acceleration;
  player.position.x += player.velocity.x;
  player.position.y += player.velocity.y;
  player.velocity.x *= damping;
  player.velocity.y *= damping;
};

export function simulateCandidate(snapshot, candidate, options = {}) {
  const state = adaptAuthoritativeState(snapshot);
  const branch = clone(state);
  let player = branch.players.find((p) => String(p.id) === String(options.playerId ?? candidate.playerId) || p.id === options.playerId);
  const horizon = Math.max(1, Math.min(HORIZON, number(options.horizon, HORIZON)));
  const path = [clone(branch.ball.position)];
  const initialPlayerPosition = clone(player?.position || branch.ball.position);
  const setupPath = Array.isArray(candidate.setupPath) && candidate.setupPath.length
    ? candidate.setupPath.map(clone)
    : (player ? [clone(player.position)] : [clone(branch.ball.position)]);
  const configuredKickStrength = options.kickSpeed ?? branch.stadium?.playerPhysics?.kickStrength;
  const speed = number(configuredKickStrength, number(player?.kickStrength ?? player?.kickRate, 5));
  const setupTicks = Math.max(0, Math.floor(number(candidate.setupTicks)));
  // Every preview starts at the exact paused replay state. This prevents the
  // renderer from inventing a first movement frame or snapping the ball.
  const previewFrames = [{ tickOffset: 0, phase: setupTicks > 0 ? 'setup' : 'release', player: clone(player?.position || initialPlayerPosition), ball: clone(branch.ball.position) }];
  const actualPlayerPath = [clone(initialPlayerPosition)];
  const positionAtSetupTick = (tick) => {
    if (setupPath.length === 1) return clone(setupPath[0]);
    const progress = Math.max(0, Math.min(1, tick / Math.max(1, setupTicks)));
    const index = Math.min(setupPath.length - 1, Math.round(progress * (setupPath.length - 1)));
    return clone(setupPath[index]);
  };
  const initialOffset = player ? subtract(branch.ball.position, player.position) : { x: 0, y: 0 };
  let setupCompleted = setupTicks <= horizon;
  for (let tick = 0; tick < Math.min(setupTicks, horizon); tick += 1) {
    const waypoint = positionAtSetupTick(tick + 1);
    integratePlayer(player, waypoint, branch.stadium?.playerPhysics);
    const previousBallPosition = { ...branch.ball.position };
    // Keep the ball in the carrier's control during the visual setup. The
    // release position is still validated against the strict contact gap.
    if (player && candidate.type !== 'retain') {
      const holdDirection = normalise(subtract(branch.ball.position, player.position));
      const holdDistance = player.radius + branch.ball.radius + 0.75;
      branch.ball.position = add(player.position, scale(holdDirection, holdDistance));
    } else if (player) {
      branch.ball.position = add(player.position, initialOffset);
    }
    // Carrying transfers the carrier's displacement into the ball's current
    // velocity. The later kick adds its impulse to this velocity, matching
    // HaxBall's contact behavior instead of resetting the ball speed.
    branch.ball.velocity = subtract(branch.ball.position, previousBallPosition);
    path.push(clone(branch.ball.position));
    actualPlayerPath.push(clone(player?.position || waypoint));
    previewFrames.push({ tickOffset: tick + 1, phase: 'setup', player: clone(player?.position || waypoint), ball: clone(branch.ball.position) });
  }
  if (player && candidate.kickPosition) {
    const finalGap = distance(player.position, branch.ball.position) - player.radius - branch.ball.radius;
    setupCompleted = setupCompleted && finalGap < KICK_CONTACT_GAP;
  }
  const kickDirection = player
    ? normalise({ x: branch.ball.position.x - player.position.x, y: branch.ball.position.y - player.position.y })
    : candidate.direction;
  if (candidate.type !== 'retain' && setupCompleted) {
    // Kick impulse is additive to the current ball velocity in O.C; replacing
    // it causes curved/rolling balls to jump to an impossible heading.
    branch.ball.velocity.x += kickDirection.x * speed * number(branch.ball.invMass, 1);
    branch.ball.velocity.y += kickDirection.y * speed * number(branch.ball.invMass, 1);
  }
  // Native HaxBall evaluates kick input before movement/collision for a tick.
  // A carrier that walked into contact therefore releases on the following
  // tick; an already aligned carrier can still release immediately.
  const releaseTickOffset = setupCompleted
    ? setupTicks + (setupTicks > 0 && candidate.type !== 'retain' ? 1 : 0)
    : null;
  if (setupCompleted && releaseTickOffset > 0) previewFrames.push({ tickOffset: releaseTickOffset, phase: candidate.type === 'retain' ? 'hold' : 'release', player: clone(player?.position || initialPlayerPosition), ball: clone(branch.ball.position) });
  for (let tick = 0; tick < horizon; tick += 1) {
    // Retain is a live carry: the carrier keeps receiving input and the ball
    // moves only through HaxBall disc contact. Other players stay frozen for
    // the explanatory preview, while release flight advances the ball alone.
    const retainInput = candidate.type === 'retain' && player
      ? { [player.id]: { input: candidate.controls || candidate.direction || { x: 0, y: 0 } } }
      : {};
    const frozenPlayers = candidate.type === 'retain'
      ? branch.players.filter(entry => String(entry.id) !== String(player?.id)).map(entry => entry.id)
      : branch.players.map(entry => entry.id);
    const stepped = advancePhysics(branch, retainInput, { frozenPlayerIds: frozenPlayers });
    branch.ball = stepped.state.ball;
    branch.players = stepped.state.players;
    player = branch.players.find(entry => String(entry.id) === String(options.playerId ?? candidate.playerId)) || player;
    path.push(clone(branch.ball.position));
    if (setupCompleted) previewFrames.push({ tickOffset: releaseTickOffset + tick + 1, phase: 'flight', player: clone(player?.position || initialPlayerPosition), ball: clone(branch.ball.position) });
  }
  return {
    state: branch,
    path,
    playerPath: actualPlayerPath,
    previewFrames,
    candidate: clone(candidate),
    horizon,
    setupCompleted,
    releaseTickOffset,
    initialPlayerPosition
  };
}

export function rankCandidates(snapshot, simulations = []) {
  const state = adaptAuthoritativeState(snapshot);
  const width = number(state.stadium?.bg?.width ?? state.stadium?.width, 840);
  const candidatePlayerId = simulations[0]?.candidate?.playerId;
  const player = state.players.find((p) => String(p.id) === String(candidatePlayerId)) || state.players[0];
  const team = teamOf(player);
  const opponentTeam = team === 'red' ? 'blue' : 'red';
  const goal = state.goals?.[opponentTeam] || { x: team === 'blue' ? width : -width, y: 0 };
  const goalX = goal.x;
  return simulations.map((simulation) => {
    const end = simulation.state.ball.position;
    const progress = (end.x - state.ball.position.x) * (goalX >= 0 ? 1 : -1);
    const goalDistance = distance(end, goal);
    // null denotes no opponents in generateCandidates; Number(null) would
    // incorrectly turn that into zero pressure.
    const pressure = simulation.candidate.pressure == null
      ? 200
      : number(simulation.candidate.pressure, 200);
    const interceptionExposure = Math.max(number(simulation.candidate.interceptionRisk), pressure < 60 ? 1 : 0);
    const scoring = simulation.candidate.type === 'shot' ? Math.max(0, 1 - goalDistance / (width * 2)) : 0;
    const retention = simulation.candidate.type === 'retain' ? 1 : (interceptionExposure ? 0.35 : 0.8);
    const setupCost = number(simulation.candidate.setupTicks) / 10;
    const setupPenalty = simulation.setupCompleted === false ? 2 : 0;
    const lanePenalty = simulation.candidate.laneBlocked ? 2 : 0;
    const responseRisk = setupResponseRisk(state, player, simulation.candidate);
    // Delayed shots are especially vulnerable: a defender gets a reaction
    // window while the carrier walks into the kick position. Apply this to all
    // actions, with a stronger weight for shots and goal-directed passes.
    const delayWeight = simulation.candidate.type === 'shot' || simulation.candidate.goal ? 1.4 : .7;
    const setupExposure = responseRisk.setupExposure * delayWeight;
    const laneClosureRisk = responseRisk.laneClosureRisk * delayWeight;
    const score = retention * 2 + progress / width + scoring * 2 - interceptionExposure - lanePenalty
      - setupCost * 0.25 - setupExposure - laneClosureRisk * .65 - setupPenalty;
    return {
      ...simulation.candidate,
      score,
      executable: simulation.setupCompleted !== false && simulation.candidate.executable !== false,
      estimated: true,
      metrics: {
        retention, progress, scoringOpportunity: scoring, interceptionExposure, setupCost,
        setupCompleted: simulation.setupCompleted !== false, setupExposure, laneClosureRisk,
        reactionTicks: responseRisk.reactionTicks,
        releaseTickOffset: simulation.releaseTickOffset,
        laneBlocked: Boolean(simulation.candidate.laneBlocked), goalDistance
      },
      path: simulation.path,
      playerPath: simulation.playerPath || undefined,
      previewFrames: simulation.previewFrames || [],
      releaseTickOffset: simulation.releaseTickOffset,
      explanation: simulation.candidate.type === 'shot' ? 'Immediate shot trades possession risk for a scoring chance.' : 'This option balances progress with pressure and setup time.'
    };
  }).sort((a, b) => b.score - a.score);
}

export function analyzeDecision(snapshot, playerId, options = {}) {
  const state = adaptAuthoritativeState(snapshot);
  const eligible = isEligibleFutsalMap({ ...state, allowClassic: options.allowClassic === true });
  if (!eligible) {
    return {
      version: LIVE_ANALYSIS_VERSION,
      eligible: false,
      reason: 'unsupported-map',
      tick: state.tick,
      playerId,
      recommendations: [],
      best: null,
      title: '',
      recommendation: '',
      alternatives: [],
      possessionPlan: { version: POSSESSION_PLANNER_VERSION, plans: [], best: null },
      bestPlan: null
    };
  }
  const candidates = generateCandidates(state, playerId, options).map((candidate) => ({ ...candidate, playerId }));
  const simulations = candidates.map((candidate) => simulateCandidate(state, candidate, { ...options, playerId }));
  const recommendations = rankCandidates(state, simulations);
  const possessionPlan = planPossession(state, playerId, options);
  const best = recommendations[0] || null;
  const label = (entry) => entry ? String(entry.type).replace(/-/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase()) : '';
  return {
    version: LIVE_ANALYSIS_VERSION,
    eligible: true,
    tick: state.tick,
    playerId,
    recommendations,
    best: best ? { ...best, label: label(best) } : null,
    title: label(best),
    recommendation: label(best),
    alternatives: recommendations.slice(1, 3).map((entry) => ({ ...entry, label: label(entry) })),
    possessionPlan,
    bestPlan: possessionPlan.best
  };
}
